/**
 * Itemized infrastructure inventory, per GCP service, split by environment.
 *
 * WHY THIS IS A CURATED FILE AND NOT A LIVE FEED: there is no billing API
 * access. `gcloud billing accounts list` returns nothing, and the one piece of
 * code that could read real GCP cost — the BigQuery billing-export query at
 * `backend/functions/utils/llmCostAnalytics.js:503-513` — is gated behind an
 * unset `GCP_BILLING_EXPORT_TABLE` env var, so it reports `configured: false`
 * and never runs. Setting up that export is the single highest-leverage
 * change for this page; see BILLING_UNLOCK below.
 *
 * Until then this catalog carries STRUCTURE and SCALE (what exists, how much
 * of it, which environment, what makes it expensive) but not dollars. Scale
 * indicators are what let you reason about relative cost without an invoice.
 *
 * Captured 2026-08-19 by a sweep across every repo in the workspace.
 */

export const GCP_PROJECTS = {
  prod: {
    id: "hey-addy-chatgpt",
    number: "284266859441",
    label: "Production",
    // The earlier AI cost audit referred to an "addy-ai-prod" project. It does
    // not exist: that string is only a local MCP server alias in .mcp.json:82.
    note: 'Production is "hey-addy-chatgpt" — not "addy-ai-prod", which is only an MCP alias.',
  },
  dev: {
    id: "addy-ai-dev",
    number: "111911035666",
    label: "Development",
    note: "Environment is selected by git branch: main -> production, everything else -> development (backend/functions/setEnvironment.sh:11-22).",
  },
};

export const COST_DRIVER = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  NONE: "none",
};

/**
 * Each entry is one billable GCP service. `resources` is the drill-down: the
 * actual named things running inside that service.
 */
export const INFRA = [
  {
    id: "cloud-functions",
    service: "Cloud Functions",
    category: "Compute",
    what: "The backend API and every background trigger. 165 top-level exports in backend/functions/index.js.",
    costModel:
      "Billed on invocations, GB-seconds and GHz-seconds. Instances with minInstances > 0 bill continuously whether or not traffic arrives.",
    costDriver: COST_DRIVER.HIGH,
    evidence: "backend/functions/index.js; backend/functions/config/config.js",
    prod: {
      present: true,
      scale: "21 keys, 29 always-warm instances",
      resources: [
        { name: "api", detail: "minInstances 5, max 200", flag: true, ref: "config.js:877,884" },
        { name: "businessInference", detail: "minInstances 3", flag: true, ref: "config.js:952" },
        { name: "gmailServerInference", detail: "minInstances 3", flag: true, ref: "config.js:963" },
        { name: "compose", detail: "minInstances 2", ref: "config.js:932" },
        { name: "langdriveAPI", detail: "minInstances 2", ref: "config.js:966" },
        { name: "document", detail: "minInstances 2, max 500, concurrency 2, timeout 1800s (v2)", flag: true, ref: "config.js:973-974" },
        {
          name: "12 more at minInstances 1",
          detail:
            "salesforce, bonzo, rewrite, followup, knowledgeBase, application, emailSummary, search, voiceAgent, telemetry, chat, webSocket",
          ref: "config.js:895-1049",
        },
        {
          name: "3 warm instances configured against nothing",
          detail:
            "organization (config.js:985) and loan (config.js:1039) are minInstances 1 but no function of either name is deployed; backgroundReplierCronjob (config.js:1052) is minInstances 1 but its export is commented out at index.js:275. Likely paid-for and unused.",
          flag: true,
          ref: "config.js:985,1039,1052",
        },
        { name: "processingChecklist", detail: "4GiB, 1680s timeout, concurrency 1 (v2)", ref: "config.js:603-604" },
        { name: "processEmbeddingsQueue", detail: "8GB — the largest memory allocation in the repo", ref: "triggers/processEmbeddingsQueue.js:13" },
      ],
    },
    dev: {
      present: true,
      scale: "4 always-warm instances",
      resources: [
        { name: "api", detail: "minInstances 1, no maxInstances set — unbounded in dev", flag: true, ref: "config.js:546" },
        { name: "bonzo", detail: "minInstances 1", ref: "config.js:563" },
        { name: "businessInference", detail: "minInstances 1", ref: "config.js:586" },
        { name: "document", detail: "minInstances 1, max 500", flag: true, ref: "config.js:595-596" },
      ],
    },
    notes:
      "165 exports: 46 HTTPS endpoints, 56 scheduled, 51 Firestore triggers, 10 manual/webhook runners, 1 auth trigger, 1 broken. Defaults elsewhere are minInstances 0 / 300s / 1GB (config/functionOptions.js:1-4). Region is never set anywhere, so everything lands in us-central1.",
  },
  {
    id: "cloud-run",
    service: "Cloud Run",
    category: "Compute",
    what: "Containerized services: the Next.js client, the backend as a web server, email servers, the Puppeteer PDF worker, and 6 MCP servers.",
    costModel:
      "Billed per vCPU-second and GiB-second while a container is serving, plus idle time on any service with min-instances > 0.",
    costDriver: COST_DRIVER.HIGH,
    evidence: ".github/workflows/cloud-run-deploy-*.yml across 6 repos",
    prod: {
      present: true,
      scale: "8 services, 3 always-on",
      resources: [
        { name: "backend-prod", detail: "cpu 2, 8Gi, min 1, concurrency 5, timeout 1800s", flag: true, ref: "deployCloudRun.sh:24" },
        { name: "client-nextjs", detail: "cpu 1, 2Gi, min 1, concurrency 8, no max", flag: true, ref: "client cloud-run-deploy-prod.yml:120-130" },
        { name: "gmail-server", detail: "cpu 2, 8Gi, min 1, max 1, concurrency 10", flag: true, ref: "gmail-server cloud-run-deploy-prod.yml:69-73" },
        { name: "addy-puppeteer-prod", detail: "cpu 4, 4Gi, min 0, concurrency 1, max 20, timeout 3600s", ref: "puppeteer cloud-run-deploy-prod.yml:69-75" },
        { name: "microsoft-server", detail: "cpu 1, 2Gi, min 0", ref: "microsoft-server cloud-run-deploy-prod.yml:55-57" },
        { name: "addy-ask-addy-mcp-prod", detail: "cpu 1, 512Mi, min 0, max 10, concurrency 80", ref: "addy-mcp-servers workflows" },
        { name: "addy-chatgpt-mcp-prod", detail: "cpu 1, 512Mi, min 0, max 10", ref: "addy-mcp-servers workflows" },
        { name: "addy-http-stream-mcp-prod", detail: "cpu 1, 512Mi, min 0, max 10", ref: "addy-mcp-servers workflows" },
      ],
    },
    dev: {
      present: true,
      scale: "8 services, 1 always-on",
      resources: [
        { name: "client-nextjs-dev", detail: "cpu 1, 2Gi, min 1, concurrency 8 — runs 24/7 on the dev project", flag: true, ref: "client cloud-run-deploy-dev.yml:148-151" },
        { name: "backend-dev", detail: "cpu 1, 2Gi, min 0, concurrency 5", ref: "deployCloudRun.sh:34" },
        { name: "emailservice", detail: "cpu 2, 8Gi, min 0, max 1, concurrency 10", ref: "gmail-server cloud-run-deploy-dev.yml:70-74" },
        { name: "puppeteer-dev", detail: "cpu 2, 8Gi, min 0, concurrency 2, max 20, timeout 3600s", ref: "puppeteer cloud-run-deploy-dev.yml:68-73" },
        { name: "microsoftserver", detail: "cpu 1, 1Gi, min 0", ref: "microsoft-server cloud-run-deploy-dev.yml:55-57" },
        { name: "addy-ask-addy-mcp-dev", detail: "cpu 1, 512Mi, min 0, max 10", ref: "addy-mcp-servers workflows" },
        { name: "addy-chatgpt-mcp-dev", detail: "cpu 1, 512Mi, min 0, max 10", ref: "addy-mcp-servers workflows" },
        { name: "addy-http-stream-mcp-dev", detail: "cpu 1, 512Mi, min 0, max 10", ref: "addy-mcp-servers workflows" },
      ],
    },
    notes:
      "client-nextjs-dev holding min-instances 1 means a 2Gi dev container bills around the clock. Also: `client` and `client-brave-fix` are duplicate repos deploying to the SAME service names, so whichever pushes last wins.",
  },
  {
    id: "cloud-run-jobs",
    service: "Cloud Run Jobs",
    category: "Compute",
    what: "The income pipeline, launched as a job with container env overrides.",
    costModel: "Billed per vCPU/GiB-second for the duration of each job execution.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/services/IncomePipelineRunner.js:92,109",
    prod: { present: true, scale: "1 job", resources: [{ name: "income pipeline job", detail: "JobsClient.runJob() with env overrides" }] },
    dev: { present: true, scale: "1 job", resources: [{ name: "income pipeline job", detail: "same, dev project" }] },
    notes: "Reads and writes artifacts to a separate INCOME_PIPELINE_BUCKET whose name is env-only (no default in code).",
  },
  {
    id: "firestore",
    service: "Firestore",
    category: "Data",
    what: "Primary datastore. ~207 distinct collections; heavily org-scoped subcollections (organizations/{org}/loans/{loan}/...).",
    costModel:
      "Billed per document read, write and delete, plus stored bytes and index storage. Write-heavy per-event collections dominate.",
    costDriver: COST_DRIVER.HIGH,
    evidence: "backend/firestore.indexes.json; backend/functions/**",
    prod: {
      present: true,
      scale: "~207 collections, 62 composite indexes, 18 field overrides",
      resources: [
        { name: "queue-logs", detail: "One doc per Cloud Tasks enqueue across 19 queues — the single largest write amplifier", flag: true, ref: "services/queue.js:642,947,1233" },
        { name: "notification-traces", detail: "One trace doc per notification attempt, plus in-place updates", flag: true, ref: "services/Trace.js:11,25,36" },
        { name: "scores-by-thread", detail: "Per-email-thread scoring, written by a 2-min and a 10-min cron", flag: true, ref: "triggers/backgroundEmailProcessor.js:18,35" },
        { name: "slidingWindowEvents", detail: "One doc per rate-limit event", ref: "services/SlidingWindow.js:4" },
        { name: "llm-response-cache", detail: "One doc per cached LLM call", ref: "services/LLMCache.js:26" },
        { name: "document-ai-cache", detail: "Per-document OCR cache", ref: "services/GoogleDrive.js:672,704,718" },
        { name: "langfuseAnalyticsRollups", detail: "Hourly LLM-analytics rollups (nightly 02:00 UTC cron)", ref: "utils/langfuseRollups.js:3" },
        { name: "loans/{loan}/history", detail: "Per-field-change audit rows; 5 composite indexes", ref: "models/LoanHistory.js:129" },
        { name: "guideline-inbound-events", detail: "Per-inbound-email audit — cron runs every 1 minute", flag: true, ref: "triggers/guidelineInboundGmailCron.js:35" },
        { name: "usage-rollups", detail: "Daily per-user rollup docs", ref: "services/usageAnalytics/rollupRepository.js:5,28" },
        { name: "meridianlink-api-usage", detail: "Per-API-call usage metering", ref: "services/integrations/meridianlink/MeridianLink.js:63" },
        { name: "extendAiUsageLogs", detail: "One usage log per ExtendAI call", ref: "services/ExtendAI.js:30" },
        { name: "Most-indexed groups", detail: "loans-thin (8), syncHistory (6), history (5), then borrowers/documents/loans/documentDownloadJobs/processingChecklistPerformanceRuns (3 each)", ref: "firestore.indexes.json" },
      ],
    },
    dev: { present: true, scale: "Same schema, lower volume", resources: [{ name: "addy-ai-dev Firestore", detail: "Same collections and indexes as prod" }] },
    notes:
      "Several of the highest-write collections appear in `fieldOverrides` — index exemptions already applied to cut write cost. queue-logs is the one worth auditing first: every single async task writes one.",
  },
  {
    id: "cloud-tasks",
    service: "Cloud Tasks",
    category: "Data",
    what: "The real async fan-out layer for the whole backend — 19 named queues.",
    costModel: "Billed per task operation. Cheap per unit, but every task also writes a queue-logs Firestore doc.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: "backend/functions/services/queue.js:2,125,362-396",
    prod: {
      present: true,
      scale: "19 queues, us-central1",
      resources: [
        {
          name: "19 queues",
          detail:
            "processing-checklist, document-classification, document-text-extraction, borrower-approval-conditions, arive-sync, lendingpad-sync, encompass-sync, encompass-epc-sync, finmo-sync, document-import, lead-extraction, document-thumbnail, knowledge-base, gohighlevel-sync, document-download, income-pipeline, ai-pipeline-eval, meridianlink-sync, drive-mirror",
          ref: "services/queue.js:362-396",
        },
      ],
    },
    dev: { present: true, scale: "Same 19 queues", resources: [{ name: "19 queues", detail: "Mirrored on the dev project" }] },
    notes: "There is NO Pub/Sub in this codebase — no topics, no @google-cloud/pubsub dependency. Cloud Tasks is the async layer.",
  },
  {
    id: "cloud-scheduler",
    service: "Cloud Scheduler",
    category: "Compute",
    what: "56 exported scheduled functions, each provisioning a Cloud Scheduler job and an internal firebase-schedule-* topic.",
    costModel:
      "Billed per job per month (the first few are free). The real cost is the compute each firing triggers, not the scheduler itself.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: "56 exported pubsub.schedule definitions across backend/functions/triggers/",
    prod: {
      present: true,
      scale: "56 scheduled jobs",
      resources: [
        { name: "guidelineInboundGmailCronJob", detail: "Every 1 minute — 43,200 firings/month", flag: true, ref: "triggers/guidelineInboundGmailCron.js:35" },
        { name: "incidentAlertEscalationCron", detail: "Every 1 minute", flag: true, ref: "triggers/incidentAlertEscalationCron.js:20" },
        { name: "executeComputerCronJobs", detail: "Every 2 minutes, writes one Firestore doc per run", flag: true, ref: "triggers/executeComputerCronJobs.js:18" },
        { name: "liveChatNudgeCron", detail: "Every 2 minutes", flag: true, ref: "triggers/liveChatNudgeCron.js:24" },
        { name: "6 jobs at every 5 minutes", detail: "demoPersonalizationSync, retryFailedExtractionInBackground, incomePipelineSheetPoller, zoomDemoAlternativeHosts, driveMirrorSafetyNetCron, pollHelpDocBuilds, processingChecklistWatchdog, downloadJobZombieReaper", ref: "triggers/" },
        { name: "adoptionCron", detail: "Hourly + every 30 minutes, writes snapshot docs", ref: "triggers/adoptionCron.js:386-406" },
        { name: "llmCostThresholdChecker", detail: "Every 3 hours", ref: "triggers/llmCostThresholdChecker.js:126" },
        { name: "langfuseAnalyticsDailyRollup", detail: "Daily 02:00 UTC", ref: "triggers/langfuseAnalyticsDailyRollup.js:259" },
        { name: "11 dead schedules still in the tree", detail: "Defined but never exported — runDailyUsageBilling, syncLoanApplications, cleanupStaleQueueTasks, backgroundEmailProcessor, backgroundReplierCronjob, dailyMixpanelUsageReport, dailyJiraEngineeringReport and others. No cost, but they mislead anyone auditing schedules.", ref: "triggers/" },
      ],
    },
    dev: { present: true, scale: "56 scheduled jobs", resources: [{ name: "Same definitions", detail: "Dev crons fire on the same schedules unless individually gated" }] },
    notes:
      "Sub-5-minute crons are what to scrutinize: two 1-minute jobs and two 2-minute jobs fire ~130,000 times a month between them, each billing compute and usually writing Firestore.",
  },
  {
    id: "cloud-storage",
    service: "Cloud Storage",
    category: "Data",
    what: "Loan documents, user files, PDF renders, announcements, eval reports.",
    costModel: "Billed on stored GB/month, plus operations and egress.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: "backend/functions/config/config.js:233-234,376",
    prod: {
      present: true,
      scale: "1 primary bucket",
      resources: [
        { name: "hey-addy-chatgpt.appspot.com", detail: "All loan documents under organizationData/{orgId}/files/loan-documents/…", ref: "config.js:233-234" },
        { name: "INCOME_PIPELINE_BUCKET", detail: "Separate bucket for income Cloud Run Job artifacts — name is env-only, not in code", ref: "services/IncomePipelineRunner.js:198" },
      ],
    },
    dev: { present: true, scale: "1 primary bucket", resources: [{ name: "addy-ai-dev.appspot.com", detail: "Dev equivalent", ref: "config.js:233-234" }] },
    notes:
      "Docs reference addy-backend.appspot.com and addy-backend-backups, but those appear nowhere in code and look stale.",
  },
  {
    id: "firebase-hosting",
    service: "Firebase Hosting",
    category: "Hosting",
    what: "Static hosting for the marketing site, admin dashboard, sidebar, help center and external API, plus Cloud Run rewrites.",
    costModel: "Billed on stored GB and transferred GB. Usually a small line item.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/setEnvironment.sh:14-21; firebase.json across 5 repos",
    prod: {
      present: true,
      scale: "7 sites",
      resources: [
        { name: "hey-addy", detail: "Primary site" },
        { name: "langdrive", detail: "" },
        { name: "addy-ai-external-api", detail: "" },
        { name: "addy-mission-control", detail: "internal-admin-dashboard" },
        { name: "addy-sidebar", detail: "sidebar-chat" },
        { name: "addy-help-center", detail: "addy-help-center" },
        { name: "document (rewrite)", detail: "Hosting rewrite to Cloud Run serviceId 'document'", ref: "backend/firebase.json:39-40" },
      ],
    },
    dev: {
      present: true,
      scale: "3 sites",
      resources: [
        { name: "hey-addy-dev", detail: "" },
        { name: "langdrive-dev", detail: "" },
        { name: "addy-ai-external-api-dev", detail: "" },
      ],
    },
    notes: "addy-help-center also spins up a PR preview channel per pull request, expiring after 7 days.",
  },
  {
    id: "firebase-auth",
    service: "Firebase Auth / Identity Platform",
    category: "Identity",
    what: "End-user and admin authentication across every surface.",
    costModel:
      "Free below the Identity Platform threshold; billed per monthly active user above it. MFA/SMS verification bills separately.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/services/firebaseAuth.js; internal-admin-dashboard/src/firebase.js",
    prod: { present: true, scale: "All product users", resources: [{ name: "Firebase Auth", detail: "admin.auth() server-side, firebase/auth client-side" }, { name: "Identity Toolkit REST", detail: "signInWithPassword used by the ChatGPT MCP OAuth bridge", ref: "addy-mcp-servers/servers/chatgpt/routes/oauth.js:207" }] },
    dev: { present: true, scale: "Dev users", resources: [{ name: "Firebase Auth", detail: "Separate dev user pool" }] },
    notes: "There are login-mfa-challenges and login-trusted-devices collections, so MFA is in play — SMS-based MFA would bill through the telephony provider.",
  },
  {
    id: "iam",
    service: "IAM & Service Accounts",
    category: "Identity",
    what: "Service accounts for CI/CD, Google Drive access, and server-to-server auth.",
    costModel: "IAM itself is free. Listed because it is real infrastructure surface with a security cost, not a dollar cost.",
    costDriver: COST_DRIVER.NONE,
    evidence: "client/lib/googleDrive/serviceAccountEmail.ts:14-15",
    prod: {
      present: true,
      scale: "3+ service accounts",
      resources: [
        { name: "github-actions@hey-addy-chatgpt", detail: "CI/CD deploys" },
        { name: "google-drive@hey-addy-chatgpt", detail: "Drive service account", ref: "client/lib/googleDrive/serviceAccountEmail.ts:14" },
      ],
    },
    dev: {
      present: true,
      scale: "3+ service accounts",
      resources: [
        { name: "github-actions@addy-ai-dev", detail: "CI/CD deploys" },
        { name: "google-drive@addy-ai-dev", detail: "Drive service account", ref: "client/lib/googleDrive/serviceAccountEmail.ts:15" },
        { name: "gmail-server-dev@addy-ai-dev", detail: "Gmail server" },
      ],
    },
    notes:
      "SECURITY, not cost: service-account JSON keys are committed at gmail-server/src/config/firebase_secrets_json.json and puppeteer/config/firebaseServiceAccDev.json. Worth rotating.",
  },
  {
    id: "secret-manager",
    service: "Secret Manager",
    category: "Identity",
    what: "Stores env bundles pulled down during developer onboarding and by the Python servers at runtime.",
    costModel: "Billed per active secret version per month, plus access operations. Small.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/scripts/setup/step3-fetch-secrets.js:3,19,40",
    prod: { present: true, scale: "Runtime credential loading", resources: [{ name: "Python server secrets", detail: "gmail-server and microsoft-server load credentials at runtime" }] },
    dev: {
      present: true,
      scale: "5 secrets",
      resources: [
        { name: "addy-env-dev", detail: "" },
        { name: "addy-runtimeconfig-dev", detail: "" },
        { name: "addy-openai-config-dev", detail: "" },
        { name: "addy-firebase-service-acc-dev", detail: "" },
        { name: "addy-google-drive-service-acc-dev", detail: "" },
      ],
    },
    notes: "A devDependency in the backend only — never imported from runtime Node code. firebase-functions v2 `defineSecret` is not used anywhere.",
  },
  {
    id: "cloud-logging",
    service: "Cloud Logging & Error Reporting",
    category: "Observability",
    what: "Log ingestion from every function and Cloud Run service, plus grouped-error triage tooling.",
    costModel:
      "Billed per GiB ingested beyond the free monthly allotment, plus retention. High-volume logging from 165 functions adds up quietly.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: "backend/functions/routes/admin.js:3337; scripts/gcpErrorFixer.js:198",
    prod: {
      present: true,
      scale: "All services",
      resources: [
        { name: "logging.googleapis.com/v2/entries:list", detail: "Admin endpoints surface stale queue tasks and non-retryable failures", ref: "routes/admin.js:2336,3391,3409,3553" },
        { name: "Cloud Error Reporting", detail: "clouderrorreporting.googleapis.com/v1beta1 for grouped-error triage", ref: "routes/admin.js:2107,2122" },
      ],
    },
    dev: { present: true, scale: "All services", resources: [{ name: "Same log sinks", detail: "Dev project logging" }] },
    notes:
      "@google-cloud/logging-winston is declared in backend/functions/package.json:61 but never imported — dead dependency.",
  },
  {
    id: "artifact-registry",
    service: "Artifact Registry",
    category: "Build",
    what: "Container images for every Cloud Run deploy.",
    costModel: "Billed on stored GB. Cost grows silently with retained image history unless a cleanup policy exists.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/deployCloudRun.sh:46,49; 10 Dockerfiles across the workspace",
    prod: { present: true, scale: "repo: cloud-run-prod", resources: [{ name: "cloud-run-prod", detail: "*-docker.pkg.dev image repository" }] },
    dev: { present: true, scale: "repo: cloud-run-dev", resources: [{ name: "cloud-run-dev", detail: "*-docker.pkg.dev image repository" }] },
    notes:
      "10 Dockerfiles build into these repos. Several images are heavy (gmail-server bundles PyTorch CPU + Tesseract; puppeteer bundles Chromium), so retained tags are not small.",
  },
  {
    id: "bigquery",
    service: "BigQuery",
    category: "Data",
    what: "Intended home of the GCP billing export. Query code exists but is dormant.",
    costModel: "Billed on bytes scanned per query plus storage.",
    costDriver: COST_DRIVER.NONE,
    evidence: "backend/functions/utils/llmCostAnalytics.js:503-513,535",
    prod: { present: false, scale: "Not configured", resources: [{ name: "GCP_BILLING_EXPORT_TABLE", detail: "Env var unset — the code returns configured:false and never queries", flag: true }] },
    dev: { present: false, scale: "Not configured", resources: [] },
    notes:
      "THIS IS THE UNLOCK. The billing-export query is already written; it just has no table to point at. Enabling the export turns most 'Unknown' rows on this page into real numbers.",
  },
  {
    id: "maps-platform",
    service: "Google Maps Platform",
    category: "APIs",
    what: "Places autocomplete/details and Geocoding for address handling and lead enrichment.",
    costModel:
      "Billed per API call at published SKU rates, with a monthly credit. Bills under GCP but is a separate SKU family from everything else here.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: "backend/functions/routes/googleAddressUtils.js:30",
    prod: {
      present: true,
      scale: "2 API families",
      resources: [
        { name: "Places API", detail: "Autocomplete + details", ref: "routes/googleAddressUtils.js:30; services/mismoExport/mismoFieldResolver.js:537,559" },
        { name: "Geocoding API", detail: "Called from lead enrichment and the email indexer", ref: "utils/leadDataUtils.js:200; triggers/searchIndexEmailProcessor.js:80" },
      ],
    },
    dev: { present: true, scale: "Same APIs", resources: [{ name: "Places + Geocoding", detail: "Dev project keys" }] },
    notes:
      "Easy to miss on a bill because it lands under GCP rather than a named vendor. A geocode-cache collection exists, which suggests call volume was already a concern.",
  },
  {
    id: "workspace-apis",
    service: "Google Workspace APIs",
    category: "APIs",
    what: "Sheets, Drive, Calendar, Gmail, Docs and Tasks, accessed via service accounts.",
    costModel:
      "No per-call charge; governed by quotas. Cost is the Workspace seats themselves, not the API.",
    costDriver: COST_DRIVER.NONE,
    evidence: "backend/functions/services/GoogleSheetsService.js:1; services/GoogleDrive.js:1",
    prod: {
      present: true,
      scale: "6 API surfaces",
      resources: [
        { name: "Drive", detail: "Document mirroring, driveMirror/driveFileIndex collections" },
        { name: "Sheets", detail: "Income pipeline + FDE assignment sheets" },
        { name: "Calendar", detail: "Demo scheduling, Meet whitelist" },
        { name: "Gmail", detail: "Guideline inbound email client" },
        { name: "Docs / Tasks", detail: "Admin tooling" },
      ],
    },
    dev: { present: true, scale: "Same", resources: [{ name: "Same APIs", detail: "Dev service accounts" }] },
    notes: "Quota-limited rather than billed, so it is a reliability risk more than a cost risk.",
  },
  {
    id: "firebase-rtdb",
    service: "Firebase Realtime Database",
    category: "Data",
    what: "A second datastore used alongside Firestore.",
    costModel: "Billed on GB stored and GB downloaded — a different model from Firestore's per-operation pricing.",
    costDriver: COST_DRIVER.LOW,
    evidence: "backend/functions/services/firebaseRealtimeDatabase.js:3,12",
    prod: { present: true, scale: "1 instance", resources: [{ name: "hey-addy-chatgpt-default-rtdb", detail: "", ref: "config.js:223-226" }] },
    dev: { present: true, scale: "1 instance", resources: [{ name: "addy-ai-dev-default-rtdb", detail: "", ref: "config.js:223-226" }] },
    notes: "Running both RTDB and Firestore means two storage bills and two consistency models.",
  },
  {
    id: "github-actions",
    service: "GitHub Actions",
    category: "Build",
    what: "44 workflows across 12 repos. Not GCP, but a real recurring compute bill.",
    costModel:
      "Billed per runner-minute beyond the plan's included minutes. Long e2e suites and Docker builds dominate.",
    costDriver: COST_DRIVER.MEDIUM,
    evidence: ".github/workflows across 12 repos",
    prod: {
      present: true,
      scale: "44 workflows",
      resources: [
        { name: "backend (13 workflows)", detail: "Includes e2e suites with 90-180 minute timeouts", flag: true },
        { name: "E2E Full Suite", detail: "timeout-minutes 180, manual dispatch", ref: "backend/.github/workflows/e2e-full-*.yml:22" },
        { name: "E2E Atomic Suite", detail: "timeout-minutes 90, manual dispatch", ref: "backend/.github/workflows/e2e-atomic-*.yml:22" },
        { name: "unit-tests.yml + lint.yml", detail: "Trigger on push to ALL branches with no path filter", flag: true, ref: "backend/.github/workflows/unit-tests.yml:4" },
        { name: "addy-mcp-servers (6)", detail: "Docker buildx + Cloud Run deploy per push" },
        { name: "client + client-brave-fix (8)", detail: "Duplicate repos running duplicate builds" },
      ],
    },
    dev: { present: true, scale: "Same workflows", resources: [{ name: "Shared", detail: "Dev and prod deploys use the same workflow set" }] },
    notes:
      "No scheduled workflows and no matrix builds anywhere — all 44 are push/PR/dispatch driven, single-configuration. Biggest waste candidates: backend unit-tests + lint firing on every branch push, and client/client-brave-fix duplicating every build.",
  },
];

/** GCP services checked for and confirmed NOT in use — so nobody re-audits them. */
export const INFRA_NOT_USED = [
  { name: "Pub/Sub", note: "No topics, no @google-cloud/pubsub dependency. Cloud Tasks is the async layer." },
  { name: "Cloud Build", note: "Docs and commented-out shell lines only; no cloudbuild.yaml exists. Images build locally then push." },
  { name: "Cloud Monitoring SDK", note: "@google-cloud/monitoring never imported. Only a console alert URL is referenced." },
  { name: "Cloud Vision / Translate / Speech-to-Text", note: "No usage. Only Text-to-Speech is used." },
  { name: "Cloud CDN / Load Balancer / Cloud Armor", note: "No configuration anywhere in the workspace." },
  { name: "Firebase Remote Config / App Check / FCM", note: "None used." },
  { name: "@google-cloud/aiplatform", note: "Declared in backend/functions/package.json:58, never imported — dead dependency." },
  { name: "@google-cloud/logging-winston", note: "Declared at package.json:61, never imported — dead dependency." },
];

/**
 * The concrete steps that would replace "Unknown" with real dollars, ordered by
 * leverage. Kept here rather than in prose so the page can render it as a
 * checklist.
 */
export const BILLING_UNLOCK = [
  {
    step: "Enable the GCP billing export to BigQuery and set GCP_BILLING_EXPORT_TABLE",
    impact: "Unlocks every GCP line on this page at once — Vertex, Document AI, Functions, Cloud Run, Firestore, Storage, Maps.",
    effort: "Low — the query code already exists at backend/functions/utils/llmCostAnalytics.js:503-513 and is gated only on this env var.",
    priority: "P0",
  },
  {
    step: "Grant roles/billing.viewer on billing account 01756D-4B5D16-8F4472",
    impact: "Makes the Vertex AI Provisioned Throughput commitment visible — potentially the single largest AI line item.",
    effort: "Low — an IAM grant.",
    priority: "P0",
  },
  {
    step: "Add an Anthropic Admin API key with usage/cost read",
    impact: "Replaces the Langfuse-derived floor with the real invoice total.",
    effort: "Low",
    priority: "P1",
  },
  {
    step: "Add Azure Cost Management Reader on the Azure subscription",
    impact: "Azure OpenAI is currently a complete blind spot.",
    effort: "Low",
    priority: "P1",
  },
  {
    step: "Fix the Langfuse model-alias pricing bug",
    impact: "Gemini cost is understated ~30-40% because langfuseTracing.js:20 maps gemini-3-flash to gemini-2.5-flash pricing.",
    effort: "Low — a config change plus registering prices for gemini-3-flash-preview and gemini-3.1-pro-preview.",
    priority: "P1",
  },
  {
    step: "Add read-only API keys for Stripe, Twilio, Telnyx, HubSpot, Sentry, PostHog and Mixpanel",
    impact: "Closes the non-AI vendor tail found in the 2026-08-19 sweep.",
    effort: "Medium — one integration per vendor.",
    priority: "P2",
  },
];
